import React, { useEffect, useLayoutEffect, useMemo, useState } from "react";
import { Link, useLocation, useParams, useSearchParams } from "react-router-dom";
import BanquetDetailExperience from "../components/banquet/BanquetDetailExperience.jsx";
import WhatsAppInquiryBar from "../components/banquet/WhatsAppInquiryBar.jsx";
import { getPublishedBanquetBySlug } from "../services/publicBanquetService.js";
import { calculateDistanceKm } from "../utils/calculateDistance.js";
import { getBanquetNonVegPrice, getBanquetVegPrice, getResolvedPincode } from "../utils/banquetFilters.js";
import { buildBanquetFaqs } from "../utils/banquetFaq.js";
import { isPubliclyVisibleBanquet } from "../utils/banquetPublication.js";

export default function BanquetDetailPage() {
  const { slug } = useParams();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const banquetTestModeEnabled = false;
  const [banquet, setBanquet] = useState(null);
  const [loadState, setLoadState] = useState({ isLoading: true, error: "" });
  const [selectedContext, setSelectedContext] = useState(null);
  const searchPincode = searchParams.get("pincode") || location.state?.searchContext?.pincode || "";
  const searchRadius = searchParams.get("radius") || location.state?.searchContext?.radius || "";
  const resolvedPincode = getResolvedPincode(searchPincode);
  const hasCoordinates = Number.isFinite(banquet?.latitude) && Number.isFinite(banquet?.longitude);
  const distanceKm = resolvedPincode && hasCoordinates
    ? calculateDistanceKm(resolvedPincode, { latitude: banquet?.latitude, longitude: banquet?.longitude })
    : null;
  const listingSearchParams = new URLSearchParams();

  if (searchPincode) {
    listingSearchParams.set("pincode", searchPincode);
  }

  if (searchRadius) {
    listingSearchParams.set("radius", searchRadius);
  }

  if (banquetTestModeEnabled) {
    listingSearchParams.set("banquetTestMode", "1");
  }

  const listingUrl = listingSearchParams.toString() ? `/banquets?${listingSearchParams.toString()}` : "/banquets";
  const banquetName = banquet?.name || "Banquet venue";
  const sourceLabel = banquet?.googleSourceLabel || "Verified DecorZoom listing";

  const pageUrl = useMemo(() => {
    if (typeof window === "undefined") {
      return `${location.pathname}${location.search}`;
    }
    return `${window.location.origin}${location.pathname}${location.search}`;
  }, [location.pathname, location.search]);

  const canonicalUrl = typeof window !== "undefined"
    ? `${window.location.origin}/banquets/${slug}`
    : `https://www.decorzoom.in/banquets/${slug}`;
  const locality = banquet?.locality || banquet?.locationDetails?.locality || "";
  const city = banquet?.city || banquet?.locationDetails?.city || "";
  const locationLabel = [locality, city].filter(Boolean).join(", ");
  const seoTitle = banquet
    ? `${banquet.name}${locationLabel ? ` - Banquet Hall in ${locationLabel}` : ""} | DecorZoom`
    : "Banquet Venue | DecorZoom";
  const minGuests = banquet?.capacityDetails?.seatingCapacity || banquet?.guestMin;
  const maxGuests = banquet?.capacityDetails?.floatingCapacity || banquet?.guestMax;
  const guestRangeLabel = minGuests || maxGuests ? `${minGuests || ""}-${maxGuests || ""} guests`.replace(/^-/, "Up to ") : "";
  const vegPrice = banquet ? getBanquetVegPrice(banquet) : null;
  const nonVegPrice = banquet ? getBanquetNonVegPrice(banquet) : null;
  const startingPrice = [vegPrice, nonVegPrice].filter((value) => Number.isFinite(value)).sort((a, b) => a - b)[0];
  const seoDescription = banquet
    ? `Book ${banquet.name}, a banquet venue in ${locationLabel || "Delhi NCR"}${guestRangeLabel ? ` for ${guestRangeLabel}` : ""}${startingPrice ? ` starting at Rs. ${startingPrice} per plate` : ""}. Check live availability, pricing and photos on DecorZoom.`
    : "Explore verified banquet halls and venues in Delhi NCR with live pricing and availability on DecorZoom.";
  const seoImages = (banquet?.images || [])
    .filter(Boolean)
    .map((image) => (image.startsWith("http") ? image : `https://www.decorzoom.in${image}`));
  const ratingValue = Number.isFinite(banquet?.googleRating) ? banquet.googleRating : null;
  const ratingCount = Number.isFinite(banquet?.googleRatingsTotal) ? banquet.googleRatingsTotal : 0;
  const priceRange = Number.isFinite(vegPrice) || Number.isFinite(nonVegPrice)
    ? `Rs. ${Math.min(vegPrice ?? Infinity, nonVegPrice ?? Infinity)}${Number.isFinite(vegPrice) && Number.isFinite(nonVegPrice) ? ` - Rs. ${Math.max(vegPrice, nonVegPrice)}` : ""}`
    : undefined;
  const faqs = useMemo(() => (banquet ? buildBanquetFaqs(banquet) : []), [banquet]);

  const localBusinessSchema = banquet
    ? {
        "@context": "https://schema.org",
        "@type": ["LocalBusiness", "EventVenue"],
        name: banquet.name,
        image: seoImages.length ? seoImages : undefined,
        address: {
          "@type": "PostalAddress",
          addressLocality: locality || undefined,
          addressRegion: city || undefined,
          addressCountry: "IN",
        },
        aggregateRating: ratingValue && ratingCount > 0
          ? { "@type": "AggregateRating", ratingValue, reviewCount: ratingCount }
          : undefined,
        priceRange,
        url: canonicalUrl,
      }
    : null;

  const faqSchema = faqs.length
    ? {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: faqs.map((faq) => ({
          "@type": "Question",
          name: faq.q,
          acceptedAnswer: { "@type": "Answer", text: faq.a },
        })),
      }
    : null;

  const breadcrumbSchema = banquet
    ? {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "Home", item: "https://www.decorzoom.in/" },
          { "@type": "ListItem", position: 2, name: "Banquets", item: "https://www.decorzoom.in/banquets" },
          { "@type": "ListItem", position: 3, name: banquet.name, item: canonicalUrl },
        ],
      }
    : null;

  const helmetTag = (
    <>
      <title>{seoTitle}</title>
      <meta name="description" content={seoDescription.slice(0, 160)} />
      <link rel="canonical" href={canonicalUrl} />
      <meta property="og:title" content={seoTitle} />
      <meta property="og:description" content={seoDescription.slice(0, 200)} />
      {seoImages[0] ? <meta property="og:image" content={seoImages[0]} /> : null}
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:type" content="business.business" />
      {localBusinessSchema ? <script type="application/ld+json">{JSON.stringify(localBusinessSchema)}</script> : null}
      {faqSchema ? <script type="application/ld+json">{JSON.stringify(faqSchema)}</script> : null}
      {breadcrumbSchema ? <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script> : null}
    </>
  );

  useLayoutEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
  }, [slug, location.search]);

  useEffect(() => {
    let isMounted = true;
    const loadBanquet = async () => {
      try {
        const publishedBanquet = await getPublishedBanquetBySlug(slug);
        if (!isMounted) return;
        setBanquet(publishedBanquet);
        setLoadState({ isLoading: false, error: "" });
      } catch (error) {
        if (!isMounted) return;
        setBanquet(null);
        setLoadState({
          isLoading: false,
          error: "The requested banquet detail page is not available right now.",
        });
      }
    };

    loadBanquet();

    return () => {
      isMounted = false;
    };
  }, [slug]);

  if (loadState.isLoading) {
    return (
      <>
        {helmetTag}
        <div className="mx-auto flex min-h-[70vh] max-w-3xl flex-col items-center justify-center px-4 text-center">
          <h1 className="text-3xl font-semibold text-slate-900">Loading banquet details</h1>
          <p className="mt-3 text-slate-600">Fetching verified banquet listing.</p>
        </div>
      </>
    );
  }

  if (!banquet || !isPubliclyVisibleBanquet(banquet)) {
    return (
      <>
        {helmetTag}
        <div className="mx-auto flex min-h-[70vh] max-w-3xl flex-col items-center justify-center px-4 text-center">
          <h1 className="text-3xl font-semibold text-slate-900">Banquet not found</h1>
          <p className="mt-3 text-slate-600">{loadState.error || "The requested banquet detail page is not available."}</p>
          <Link to={listingUrl} className="mt-6 rounded-full bg-emerald-600 px-5 py-3 text-sm font-semibold text-white">
            Back to Banquets
          </Link>
        </div>
      </>
    );
  }

  const handleDateSelect = (date, slotState) => {
    const slotLabel = !slotState.lunch && !slotState.dinner
      ? "Both lunch and dinner available"
      : slotState.lunch && !slotState.dinner
        ? "Lunch booked, dinner available"
        : !slotState.lunch && slotState.dinner
          ? "Lunch available, dinner booked"
          : "Fully booked";

    setSelectedContext({ date, slotLabel });
  };

  return (
    <>
      {helmetTag}
      <div className="min-h-screen overflow-x-hidden bg-[#f5f7f4] pb-36 sm:pb-28">
        <div className="mx-auto max-w-7xl px-3 pb-10 pt-6 sm:px-4 sm:pt-8">
          <div className="mb-6 flex min-w-0 flex-wrap items-center gap-3 text-sm text-slate-500">
            <Link to="/" className="hover:text-emerald-600">Home</Link>
            <span>/</span>
            <Link to={listingUrl} className="hover:text-emerald-600">Banquets</Link>
            <span>/</span>
            <span className="min-w-0 break-words text-slate-900">{banquetName}</span>
          </div>

          <div className="mt-8">
            <BanquetDetailExperience
              banquet={banquet}
              distanceKm={distanceKm}
              selectedContext={selectedContext}
              onDateSelect={handleDateSelect}
              pageUrl={pageUrl}
              sourceLabel={sourceLabel}
            />
          </div>
        </div>

        <WhatsAppInquiryBar
          banquet={banquet}
          selectedDate={selectedContext?.date}
          selectedSlot={selectedContext?.slotLabel}
          pageUrl={pageUrl}
        />
      </div>
    </>
  );
}
